import type UserMarketsDialogProperties from "./UserMarketsDialog.properties.ts";
import { type ChangeEvent, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { toastOptions } from "../../tools/helpers/ToastHelper.ts";
import type {
  MarketIOLight,
  MarketMarketIO,
  UserMarketsUserMarketsIO,
} from "../../generated/fresh-guard-api";
import type { SelectType } from "../../components/shared/select/Select.type.ts";
import { useUserMarketsSelect } from "../../apis/user-markets/useUserMarketsSelect.ts";
import { useUserMarketsUpdate } from "../../apis/user-markets/useUserMarketsUpdate.ts";
import { useTranslation } from "react-i18next";
import { useUserManagement } from "../../provider/user-management/UserManagement.hook.ts";
import { useMarketsSelect } from "../../apis/market/useMarketsSelect.ts";

export const useUserMarketsDialogController = (
  props: UserMarketsDialogProperties,
) => {
  const { t } = useTranslation();
  const { updateMarkets } = useUserManagement();
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);

  const { data: markets, isLoading: marketsLoading } = useMarketsSelect();
  const { data: userMarkets, isLoading: userMarketsLoading } =
    useUserMarketsSelect(props.user?.username);
  const { mutate, isPending } = useUserMarketsUpdate();

  const handleSave = () => {
    const marketsArray: MarketIOLight[] = selectedMarkets
      .map((id) => {
        const marketId = parseInt(id, 10);
        return isNaN(marketId) ? null : ({ id: marketId } as MarketIOLight);
      })
      .filter((market): market is MarketIOLight => market !== null);

    const userMarketsUserMarketsIO: UserMarketsUserMarketsIO = {
      username: props.user?.username,
      markets: marketsArray,
    };
    mutate(
      { userMarketsUserMarketsIO },
      {
        onSuccess: () => {
          if (markets) {
            toast.success(t("success.userSaved"), toastOptions);
            updateMarkets(markets, userMarketsUserMarketsIO);
            props.handleDialogCloseClick();
          }
        },
      },
    );
  };

  const handleMarketsChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedMarkets(JSON.parse(event.target.value));
  };

  const prepareMarketsForSmartSelect = () => {
    return markets?.map(
      (market: MarketMarketIO) =>
        ({ label: market.name, value: market.id?.toString() }) as SelectType,
    );
  };

  useEffect(() => {
    if (userMarkets && userMarkets.markets) {
      const marketIds = userMarkets.markets
        .map((market) => market.id?.toString())
        .filter((id): id is string => id !== undefined);
      // eslint-disable-next-line
      setSelectedMarkets(marketIds);
    }
  }, [userMarkets]);

  return {
    loading: marketsLoading || userMarketsLoading || isPending,
    selectedMarkets,
    prepareMarketsForSmartSelect,
    handleSave,
    handleMarketsChange,
  };
};
